import { formatDateIn, intlTagFor, toLocale } from '@zuruflex/i18n';
import { MAX_ATTACHMENTS_PER_MESSAGE, REDACTION_MARK } from '@zuruflex/types';
import type { MessageThread, SavedReply, ThreadMessage, ThreadTopic } from '@zuruflex/types';
import { Car, Check, CheckCheck, EyeOff, Lock, Paperclip, Send, Trash2, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/cn.js';
import { Button } from '../ui/button.js';
import { UnreadDot } from '../ui/unread-dot.js';
import {
  AttachmentChip,
  AttachmentResolverProvider,
  fileKind,
  formatBytes,
  isImage,
  Lightbox,
  PhotoGrid,
  type ResolveAttachment,
} from './thread-media.js';
import { SavedReplyPicker, type SavedReplyPickerLabels } from './saved-reply-picker.js';

/**
 * The conversation, and the box you answer it in.
 *
 * All three apps render the same components, so a renter, an owner and an
 * agent are looking at one thing rather than three implementations of it.
 * What differs between them — who may write a note, whether a thread can
 * be assigned — is passed in, not decided here.
 */

/**
 * How long a run of messages by one person stays one run.
 *
 * The same half hour the phones use (`grouping.ts` in `@zuruflex/mobile-ui`),
 * so a conversation is cut into turns at the same places whichever screen
 * it is read on: somebody double-texting is one turn however long they took
 * over it, and a reply the next morning is a new one.
 */
const RUN_GAP_MS = 30 * 60 * 1000;

/** A row of the conversation as drawn: a day's heading, or one message. */
export type ThreadRowItem =
  | { kind: 'day'; key: string; at: string }
  | {
      kind: 'message';
      key: string;
      message: ThreadMessage;
      /** The oldest of its run — the one with air above it and the name. */
      firstOfRun: boolean;
      /** The newest of its run — the one the tail hangs off. */
      lastOfRun: boolean;
      /** `firstOfRun`, and somebody else's, and not the system talking. */
      showAuthor: boolean;
    };

function isMachine(message: ThreadMessage): boolean {
  return message.authorKind === 'SYSTEM' || message.authorKind === 'BOT';
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * A thread, arranged to be read: consecutive messages by one person
 * collapsed into runs, and a heading wherever the day changes.
 *
 * Oldest first, because a web list is not inverted. The rule is the phone's
 * own, so the two agree about where a turn starts. Ordered by `seq` and
 * never by time — the server numbers messages within a thread, and two
 * inserts in one millisecond sort arbitrarily by their timestamps.
 */
export function groupMessages(messages: readonly ThreadMessage[]): ThreadRowItem[] {
  const ordered = [...messages].sort((a, b) => a.seq - b.seq);
  const rows: ThreadRowItem[] = [];

  ordered.forEach((message, i) => {
    const older = ordered[i - 1];
    const newer = ordered[i + 1];
    const at = new Date(message.createdAt);

    const runsWith = (other: ThreadMessage | undefined) =>
      Boolean(
        other &&
        !isMachine(other) &&
        !isMachine(message) &&
        other.isMine === message.isMine &&
        other.authorName === message.authorName &&
        other.isPublic === message.isPublic &&
        sameDay(new Date(other.createdAt), at) &&
        Math.abs(new Date(other.createdAt).getTime() - at.getTime()) <= RUN_GAP_MS,
      );

    if (!older || !sameDay(new Date(older.createdAt), at)) {
      rows.push({ kind: 'day', key: `day-${message.id}`, at: message.createdAt });
    }

    const firstOfRun = !runsWith(older);
    rows.push({
      kind: 'message',
      key: message.id,
      message,
      firstOfRun,
      lastOfRun: !runsWith(newer),
      showAuthor: firstOfRun && !message.isMine && !isMachine(message),
    });
  });

  return rows;
}

export interface MessageBubbleProps {
  message: ThreadMessage;
  /** Starts a turn: air above it. Defaults to true, for a bubble on its own. */
  firstOfRun?: boolean;
  /** Ends a turn: the tail hangs off it. */
  lastOfRun?: boolean;
  /** Defaults to "theirs", so a lone bubble still says who wrote it. */
  showAuthor?: boolean;
  /** How far the other side has read, for the ticks under my own. */
  readBySeq?: number;
  className?: string;
}

/**
 * One message.
 *
 * The phone's bubble shape, in the web's own colours: mine is the brand
 * yellow and sits right, theirs is the well and sits left. The phones draw
 * mine in ink instead; the web keeps the yellow it has always had.
 *
 * The time and the ticks live *inside* the bubble, at the end of the last
 * line, rather than on a line of their own beneath it — which spent a whole
 * row's height on five characters and turned a conversation into a log.
 *
 * Aligned by `isMine`, which the API works out, because "mine" is a
 * different column in each of the three realms.
 */
export function MessageBubble({
  message,
  firstOfRun = true,
  lastOfRun = true,
  showAuthor,
  readBySeq,
  className,
}: MessageBubbleProps) {
  const { t, i18n } = useTranslation('common');
  const locale = toLocale(i18n.language);
  const mine = message.isMine;
  const note = !message.isPublic;
  /** Which of the message's pictures is open full size, if any. */
  const [viewing, setViewing] = useState<number | null>(null);

  // A system line is about the conversation rather than part of it, so it
  // runs down the middle and carries no bubble at all.
  if (isMachine(message)) {
    return (
      <li className={cn('flex justify-center px-4 py-2', className)}>
        <p className="max-w-prose rounded-pill bg-surface-sunken px-3 py-1 text-center text-xs text-text-muted">
          {message.body}
        </p>
      </li>
    );
  }

  const images = message.attachments.filter(isImage);
  const files = message.attachments.filter((file) => !images.includes(file));
  const hasWords = Boolean(message.body.trim());
  const author = showAuthor ?? !mine;
  const withdrawn = Boolean(message.deletedAt);

  /*
    Withdrawn by its author. Outside staff the API sends it back empty, and
    it keeps its place as a quiet line; staff get the words, marked below.
  */
  if (withdrawn && !hasWords && message.attachments.length === 0) {
    return (
      <li
        className={cn(
          'flex',
          mine ? 'justify-end' : 'justify-start',
          firstOfRun ? 'mt-6 first:mt-0' : 'mt-1.5',
          className,
        )}
      >
        <p className="rounded-[1.125rem] border border-border-strong px-3 py-1.5 text-sm italic text-text-muted">
          {t('messaging.deleted')}
        </p>
      </li>
    );
  }

  const stamp = (
    <Stamp
      message={message}
      mine={mine}
      readBySeq={readBySeq}
      time={formatTimeIn(message.createdAt, locale)}
      // On the yellow the quiet colour has to be the accent's own ink.
      onAccent={mine && !note}
    />
  );

  // A tail only on an ordinary bubble: a note is dashed and unfilled, and
  // a flare on an outline is a shape nobody reads as part of it.
  const tail = lastOfRun && !note;

  return (
    <li
      className={cn(
        'flex',
        mine ? 'justify-end' : 'justify-start',
        firstOfRun ? 'mt-6 first:mt-0' : 'mt-1.5',
        className,
      )}
    >
      <div
        className={cn(
          'flex max-w-[min(36rem,78%)] flex-col gap-0.5',
          mine ? 'items-end' : 'items-start',
        )}
      >
        {author ? (
          <p className="px-3 pb-0.5 text-[11px] font-medium text-text-muted">
            {message.authorName}
          </p>
        ) : null}

        {images.length > 0 ? (
          <>
            <PhotoGrid
              images={images}
              onOpen={setViewing}
              // No caption to hang the time under, so it sits on the picture.
              overlay={hasWords || files.length > 0 ? null : stamp}
            />
            <Lightbox
              images={images}
              index={viewing}
              onIndexChange={setViewing}
              onClose={() => setViewing(null)}
            />
          </>
        ) : null}

        {hasWords || files.length > 0 || images.length === 0 ? (
          <div
            className={cn(
              'relative min-w-0 max-w-full rounded-[1.125rem] px-3 py-1.5 text-sm leading-snug',
              note
                ? 'border border-dashed border-border-strong bg-surface text-text'
                : mine
                  ? 'bg-accent text-accent-text'
                  : 'bg-surface-sunken text-text',
              // Squared where the tail grows out, so the two read as one shape.
              tail && (mine ? 'rounded-br-[0.25rem]' : 'rounded-bl-[0.25rem]'),
            )}
          >
            {tail ? <BubbleTail mine={mine} /> : null}

            {withdrawn ? (
              <p
                className={cn(
                  'mb-1 flex items-center gap-1.5 pt-0.5 text-[11px] font-semibold uppercase tracking-wide',
                  mine && !note ? 'text-accent-text/70' : 'text-text-muted',
                )}
              >
                <Trash2 className="size-3" />
                {t('messaging.deletedPreview')}
              </p>
            ) : null}

            {note ? (
              <p className="mb-1 flex items-center gap-1.5 pt-0.5 text-[11px] font-semibold uppercase tracking-wide text-text-muted">
                <EyeOff className="size-3" />
                {t('messaging.note')}
              </p>
            ) : null}

            {files.length > 0 ? (
              <ul className={cn('flex flex-col gap-1 pt-1', hasWords ? 'pb-1' : 'pb-0.5')}>
                {files.map((file) => (
                  <li key={file.id}>
                    <AttachmentChip file={file} onAccent={mine && !note} />
                  </li>
                ))}
              </ul>
            ) : null}

            {/*
              The words, with the stamp tucked in after the last of them.

              An invisible copy of the stamp sits inline at the end of the
              text and reserves its room; the visible one is pinned to the
              bottom corner over that room. So a short message keeps its time
              on the same line, and a long one pushes it under the last line
              only when there genuinely is no space — which is how every
              phone here already reads.
            */}
            <p className="whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
              {hasWords ? withRedactions(message.body, t) : null}
              <span aria-hidden className="invisible ml-2 inline-block">
                {stamp}
              </span>
            </p>
            <span className="absolute bottom-1 right-3">{stamp}</span>
          </div>
        ) : null}

        {message.wasRedacted ? (
          <p className="flex items-center gap-1 px-3 text-[11px] text-text-muted">
            <Lock className="size-3" />
            {t('messaging.wasRedacted')}
          </p>
        ) : null}
      </div>
    </li>
  );
}

/**
 * The flare at the foot of the last bubble in a run.
 *
 * Drawn in the bubble's own colour with `currentColor`, so it follows the
 * theme with the bubble rather than holding a second copy of the palette.
 */
function BubbleTail({ mine }: { mine: boolean }) {
  return (
    <svg
      aria-hidden
      viewBox="0 0 8 13"
      className={cn(
        'absolute bottom-0 h-[13px] w-2',
        mine ? '-right-[7px] text-accent' : '-left-[7px] -scale-x-100 text-surface-sunken',
      )}
    >
      <path d="M0 0C0 6.5 2.5 11 8 13H0Z" fill="currentColor" />
    </svg>
  );
}

/**
 * The time, and what has become of my message: one tick once the server
 * has it, two once the other side has read it. No ticks under theirs — they
 * would be telling somebody what they already know.
 */
function Stamp({
  message,
  mine,
  readBySeq,
  time,
  onAccent,
}: {
  message: ThreadMessage;
  mine: boolean;
  readBySeq?: number;
  time: string;
  onAccent: boolean;
}) {
  const { t } = useTranslation('common');
  const read = readBySeq !== undefined && readBySeq >= message.seq;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 whitespace-nowrap align-baseline text-[10.5px] leading-none',
        onAccent ? 'text-accent-text/60' : 'text-text-subtle',
      )}
    >
      {message.editedAt ? <span>{t('messaging.edited')}</span> : null}
      <time dateTime={message.createdAt}>{time}</time>
      {mine && readBySeq !== undefined ? (
        read ? (
          // Blue, the shape every phone already teaches. A fixed shade: the
          // yellow under it is the same in both themes, and `info` lightens
          // in dark mode to a blue that vanishes on it.
          <CheckCheck className={cn('size-3.5', onAccent ? 'text-blue-700' : 'text-info')} />
        ) : (
          <Check className="size-3" />
        )
      ) : null}
    </span>
  );
}

/** `10:37 pm`, in the reader's own locale. */
function formatTimeIn(value: string, locale: ReturnType<typeof toLocale>): string {
  return new Intl.DateTimeFormat(intlTagFor(locale), { timeStyle: 'short' }).format(
    new Date(value),
  );
}

/**
 * Renders the marker the API leaves behind as a chip rather than as the
 * literal text `[hidden]`, which reads like a bug.
 */
function withRedactions(body: string, t: (key: string) => string): ReactNode {
  if (!body.includes(REDACTION_MARK)) return body;

  return body.split(REDACTION_MARK).flatMap((part, index) =>
    index === 0
      ? [part]
      : [
          <span
            key={index}
            className="mx-0.5 inline-flex items-center gap-1 rounded-pill bg-current/10 px-2 py-0.5 align-middle text-[11px] font-medium"
          >
            <Lock className="size-3" />
            {t('messaging.hidden')}
          </span>,
          part,
        ],
  );
}

/**
 * Which day the messages under it were sent. Today and yesterday by name,
 * everything older by date — a thread read a week later is a record, and
 * "Tuesday" without a date is only useful for about six days.
 */
function DaySeparator({ at }: { at: string }) {
  const { t, i18n } = useTranslation('common');
  const when = new Date(at);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  const label = sameDay(when, today)
    ? t('messaging.today')
    : sameDay(when, yesterday)
      ? t('messaging.yesterday')
      : formatDateIn(at, toLocale(i18n.language));

  return (
    <li className="sticky top-2 z-10 flex justify-center pb-1 pt-5 first:pt-0">
      <span className="rounded-pill border border-border-default bg-surface/90 px-3 py-1 text-[11px] font-medium text-text-muted shadow-[var(--elev-1)] backdrop-blur">
        {label}
      </span>
    </li>
  );
}

export interface MessageListProps {
  messages: ThreadMessage[];
  /** How far the other side has read, for the ticks under my own messages. */
  readBySeq?: number;
  /**
   * Turns an attachment id into a URL a browser can load — the app's
   * `api.attachmentLink`. Absent means photos cannot be drawn and files
   * cannot be opened, which is what every portal had until now.
   */
  resolveAttachment?: ResolveAttachment;
  empty?: ReactNode;
  className?: string;
}

/**
 * The conversation, oldest first, scrolled to the bottom.
 *
 * Pinned to the newest message only when the reader is already near it —
 * yanking somebody back down while they are reading last week's argument
 * is how a chat becomes unusable during the one conversation that matters.
 */
export function MessageList({
  messages,
  readBySeq,
  resolveAttachment,
  empty,
  className,
}: MessageListProps) {
  const scroller = useRef<HTMLDivElement>(null);
  const atBottom = useRef(true);
  const last = messages.at(-1)?.seq ?? 0;
  const rows = useMemo(() => groupMessages(messages), [messages]);

  useEffect(() => {
    const element = scroller.current;
    if (!element || !atBottom.current) return;
    element.scrollTop = element.scrollHeight;
  }, [last]);

  return (
    <AttachmentResolverProvider resolve={resolveAttachment}>
      <div
        ref={scroller}
        onScroll={(event) => {
          const element = event.currentTarget;
          atBottom.current = element.scrollHeight - element.scrollTop - element.clientHeight < 120;
        }}
        className={cn('min-h-0 flex-1 overflow-y-auto px-4 py-4 md:px-6', className)}
      >
        {messages.length === 0 ? (
          <div className="grid h-full place-items-center">{empty}</div>
        ) : (
          <ul className="flex min-h-full flex-col justify-end">
            {rows.map((row) =>
              row.kind === 'day' ? (
                <DaySeparator key={row.key} at={row.at} />
              ) : (
                <MessageBubble
                  key={row.key}
                  message={row.message}
                  firstOfRun={row.firstOfRun}
                  lastOfRun={row.lastOfRun}
                  showAuthor={row.showAuthor}
                  readBySeq={readBySeq}
                />
              ),
            )}
          </ul>
        )}
      </div>
    </AttachmentResolverProvider>
  );
}

/** What an upload hands back, and what a message carries it away as. */
export interface ComposerAttachment {
  key: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
}

/**
 * Everything the saved-reply picker needs, in one optional prop.
 *
 * One object rather than a `savedReplies` array beside a `savedReplyLabels`
 * bag: the picker's copy lives in the `admin` namespace and has to travel
 * with the list, and two optional props that are only meaningful together
 * is a shape that type-checks while rendering an unlabelled button.
 */
export interface ComposerSavedReplies {
  items: SavedReply[];
  labels: SavedReplyPickerLabels;
  /** The thread's own language and subject, for ordering the list. */
  locale?: string;
  topic?: ThreadTopic | null;
}

export interface MessageComposerProps {
  onSend: (
    body: string,
    options: { isPublic: boolean; attachments: ComposerAttachment[] },
  ) => void | Promise<void>;
  /**
   * Puts one file somewhere and says what it became.
   *
   * Supplied by the app rather than done here: the design system has no
   * business holding an API client, and each of the three apps already has
   * one. Absent means no paperclip.
   */
  onAttach?: (file: File) => Promise<ComposerAttachment>;
  disabled?: boolean;
  pending?: boolean;
  /** Staff on a support thread, and nobody else. */
  allowNotes?: boolean;
  /**
   * Canned answers, offered beside the paperclip. Absent means no picker —
   * the same precedent `allowNotes` sets, so the renter's composer and the
   * owner's are untouched by a staff tool.
   */
  savedReplies?: ComposerSavedReplies;
  /** Shows the "contact details are hidden" line above the box. */
  masked?: boolean;
  placeholder?: string;
  maxLength?: number;
  className?: string;
}

/** A file picked for the next message, from the pick until the send. */
interface Staged {
  id: string;
  file: File;
  /** An object URL for a picture, so it shows before it has uploaded. */
  preview: string | null;
  /** What the upload became. Absent while it is still going. */
  uploaded?: ComposerAttachment;
}

/**
 * One staged file above the box: the picture itself for a picture, a card
 * with its name and size for anything else, dimmed with a spinner until its
 * upload lands.
 */
function StagedPreview({
  item,
  removeLabel,
  uploadingLabel,
  onRemove,
}: {
  item: Staged;
  removeLabel: string;
  uploadingLabel: string;
  onRemove: () => void;
}) {
  const busy = !item.uploaded;

  return (
    <li className="group relative">
      {item.preview ? (
        <img
          src={item.preview}
          alt={item.file.name}
          className={cn(
            'size-20 rounded-[0.875rem] border border-border-default object-cover transition-opacity',
            busy && 'opacity-50',
          )}
        />
      ) : (
        <div
          className={cn(
            'flex h-20 w-52 items-center gap-2.5 rounded-[0.875rem] border border-border-default bg-surface px-3 transition-opacity',
            busy && 'opacity-60',
          )}
        >
          <span className="grid size-9 shrink-0 place-items-center rounded-[0.5rem] bg-surface-hover text-text-muted">
            <Paperclip className="size-4" />
          </span>
          <span className="min-w-0 text-xs">
            <span className="block truncate font-medium text-text">{item.file.name}</span>
            <span className="block text-text-muted">
              {fileKind({ filename: item.file.name, contentType: item.file.type })} ·{' '}
              {formatBytes(item.file.size)}
            </span>
          </span>
        </div>
      )}

      {busy ? (
        <span
          className="absolute inset-0 grid place-items-center"
          role="status"
          aria-label={uploadingLabel}
        >
          <span className="size-5 animate-spin rounded-full border-2 border-text/20 border-t-text" />
        </span>
      ) : null}

      <button
        type="button"
        aria-label={removeLabel}
        onClick={onRemove}
        className="absolute -right-1.5 -top-1.5 grid size-6 place-items-center rounded-full border border-border-default bg-surface text-text-muted shadow-[var(--elev-1)] transition-colors hover:text-text"
      >
        <X className="size-3.5" />
      </button>
    </li>
  );
}

/**
 * The reply box.
 *
 * The masking notice sits *above* the box rather than appearing after the
 * message has gone: somebody about to type a number should be told before
 * they type it, not shown a redacted version of what they sent.
 */
export function MessageComposer({
  onSend,
  onAttach,
  disabled,
  pending,
  allowNotes = false,
  savedReplies,
  masked = false,
  placeholder,
  maxLength = 4000,
  className,
}: MessageComposerProps) {
  const { t } = useTranslation('common');
  const [body, setBody] = useState('');
  const [isNote, setIsNote] = useState(false);
  const [staged, setStaged] = useState<Staged[]>([]);
  const [dragging, setDragging] = useState(false);
  const [uploadError, setUploadError] = useState<string>();
  const picker = useRef<HTMLInputElement>(null);
  const box = useRef<HTMLTextAreaElement>(null);
  /** Where the caret should land after an insert, or null for leave it be. */
  const caret = useRef<number | null>(null);

  useEffect(() => {
    if (caret.current === null) return;
    const at = caret.current;
    caret.current = null;
    box.current?.focus();
    box.current?.setSelectionRange(at, at);
  }, [body]);

  // The box grows with what is typed, up to a few lines, and then scrolls —
  // one line when empty, so the composer is a bar and not a form.
  useEffect(() => {
    const element = box.current;
    if (!element) return;
    element.style.height = 'auto';
    element.style.height = `${Math.min(element.scrollHeight, 160)}px`;
  }, [body]);

  // The previews are object URLs, and each one pins its file in memory
  // until it is revoked — on removal, on send, and here on the way out.
  const previews = useRef(new Set<string>());
  useEffect(() => {
    const held = previews.current;
    return () => held.forEach((url) => URL.revokeObjectURL(url));
  }, []);

  const full = staged.length >= MAX_ATTACHMENTS_PER_MESSAGE;
  const uploading = staged.some((item) => !item.uploaded);
  // A photograph on its own is a message. Requiring a caption is how
  // somebody ends up typing "see attached".
  const ready =
    (body.trim().length > 0 || staged.length > 0) && !disabled && !pending && !uploading;

  /**
   * Stages files and starts every upload at once.
   *
   * Each file is on screen — as its own picture, where it is one — from the
   * moment it is picked, not from the moment the upload finishes: seeing
   * the wrong photo while it is still uploading is when somebody can do
   * something about it.
   */
  function attach(list: FileList | File[] | null) {
    if (!onAttach || !list) return;
    setUploadError(undefined);

    const room = MAX_ATTACHMENTS_PER_MESSAGE - staged.length;
    const files = Array.from(list);
    if (files.length > room) setUploadError(t('messaging.attachFull'));

    const added: Staged[] = files.slice(0, Math.max(room, 0)).map((file) => {
      const preview = file.type.startsWith('image/') ? URL.createObjectURL(file) : null;
      if (preview) previews.current.add(preview);
      return { id: crypto.randomUUID(), file, preview };
    });
    setStaged((current) => [...current, ...added]);

    for (const item of added) {
      onAttach(item.file).then(
        (uploaded) =>
          setStaged((current) =>
            current.map((entry) => (entry.id === item.id ? { ...entry, uploaded } : entry)),
          ),
        (error: unknown) => {
          setUploadError(error instanceof Error ? error.message : String(error));
          unstage(item.id);
        },
      );
    }

    // So picking the same file twice in a row fires `change` both times.
    if (picker.current) picker.current.value = '';
  }

  function unstage(id: string) {
    setStaged((current) => {
      const gone = current.find((item) => item.id === id);
      if (gone?.preview) {
        URL.revokeObjectURL(gone.preview);
        previews.current.delete(gone.preview);
      }
      return current.filter((item) => item.id !== id);
    });
  }

  /**
   * Drops a saved reply in at the caret.
   *
   * At the caret and not over the box, because the usual shape of the job
   * is "Hi {name}," typed by hand, then the boilerplate, then a line of
   * their own underneath — and a picker that replaced what was already
   * there would make the agent type it twice.
   */
  function insert(text: string) {
    const element = box.current;
    const at = element ? element.selectionStart : body.length;
    const before = body.slice(0, at);
    const after = body.slice(at);
    // A blank line either side, but only where there is something to
    // separate from: a reply pasted into an empty box should not open on
    // two carriage returns.
    const piece = `${before && !before.endsWith('\n') ? '\n' : ''}${text}${after ? '\n' : ''}`;
    const next = (before + piece + after).slice(0, maxLength);
    // Parked rather than applied here: the box is controlled, so the new
    // text is not in the DOM yet and moving the caret now would move it
    // within the old value. The effect below places it once React has
    // written the value through.
    caret.current = Math.min(before.length + piece.length, next.length);
    setBody(next);
  }

  async function submit(event?: FormEvent) {
    event?.preventDefault();
    if (!ready) return;
    const text = body.trim();
    const files = staged.flatMap((item) => (item.uploaded ? [item.uploaded] : []));
    // Cleared first: a slow send that left the text in place invites the
    // second tap that the idempotency key exists to survive, and there is
    // no reason to invite it.
    setBody('');
    staged.forEach((item) => item.preview && URL.revokeObjectURL(item.preview));
    previews.current.clear();
    setStaged([]);
    await onSend(text, { isPublic: !isNote, attachments: files });
  }

  return (
    <form
      onSubmit={submit}
      className={cn('border-t border-border-default bg-surface px-4 pb-4 pt-3 md:px-6', className)}
    >
      <div>
        {masked ? (
          <p className="mb-2 flex items-start gap-1.5 px-2 text-[11px] leading-relaxed text-text-subtle">
            <Lock className="mt-0.5 size-3 shrink-0" />
            {t('messaging.maskedNotice')}
          </p>
        ) : null}

        {uploadError ? <p className="mb-2 px-2 text-xs text-danger">{uploadError}</p> : null}

        {/*
          One capsule holding everything: the staged files along its top,
          then the paperclip, the box and the send key on one line. The
          phone's composer, and the shape every chat on a desktop already
          has — a field with buttons bolted either side of it read as a form.
        */}
        <div
          // Dropped or pasted files stage exactly as picked ones do — a
          // screenshot pasted in is the commonest attachment there is.
          onDragOver={(event) => {
            if (!onAttach || !event.dataTransfer.types.includes('Files')) return;
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(event) => {
            if (!onAttach) return;
            event.preventDefault();
            setDragging(false);
            attach(event.dataTransfer.files);
          }}
          onPaste={(event) => {
            if (!onAttach || event.clipboardData.files.length === 0) return;
            event.preventDefault();
            attach(event.clipboardData.files);
          }}
          className={cn(
            'rounded-[1.5rem] border bg-surface-sunken transition-colors focus-within:border-border-strong',
            isNote ? 'border-dashed border-border-strong' : 'border-border-default',
            dragging && 'border-accent bg-accent-soft',
          )}
        >
          {staged.length > 0 ? (
            <ul className="flex flex-wrap gap-2 px-3 pt-3">
              {staged.map((item) => (
                <StagedPreview
                  key={item.id}
                  item={item}
                  removeLabel={t('messaging.removeAttachment', { name: item.file.name })}
                  uploadingLabel={t('messaging.uploading')}
                  onRemove={() => unstage(item.id)}
                />
              ))}
            </ul>
          ) : null}

          <div className="flex items-end gap-1 p-1.5">
            {onAttach ? (
              <>
                <input
                  ref={picker}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(event) => attach(event.target.files)}
                />
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="shrink-0 rounded-full text-text-muted hover:text-text"
                  disabled={disabled || full}
                  aria-label={t('messaging.attach')}
                  onClick={() => picker.current?.click()}
                >
                  <Paperclip className="size-4" />
                </Button>
              </>
            ) : null}

            {savedReplies ? (
              <SavedReplyPicker
                replies={savedReplies.items}
                labels={savedReplies.labels}
                locale={savedReplies.locale}
                topic={savedReplies.topic}
                disabled={disabled}
                onInsert={(reply) => insert(reply.body)}
              />
            ) : null}

            <textarea
              ref={box}
              value={body}
              onChange={(event) => setBody(event.target.value.slice(0, maxLength))}
              placeholder={placeholder ?? t('messaging.placeholder')}
              rows={1}
              disabled={disabled}
              aria-label={t('messaging.placeholder')}
              onKeyDown={(event) => {
                // Enter sends, Shift+Enter breaks the line. The other way round
                // loses a message every time somebody hits Enter out of habit.
                // Not while an IME is composing: that Enter picks a character.
                if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
                  event.preventDefault();
                  void submit();
                }
              }}
              className="max-h-40 min-h-10 flex-1 resize-none self-center bg-transparent px-2 py-2.5 text-sm leading-5 text-text outline-none placeholder:text-text-subtle disabled:opacity-60"
            />
            <Button
              type="submit"
              size="icon"
              className="shrink-0 rounded-full"
              disabled={!ready}
              aria-label={t('messaging.send')}
            >
              <Send className="size-4" />
            </Button>
          </div>
        </div>

        {allowNotes ? (
          <label className="mt-2 flex cursor-pointer items-center gap-2 px-2 text-xs text-text-muted">
            <input
              type="checkbox"
              checked={isNote}
              onChange={(event) => setIsNote(event.target.checked)}
              className="size-3.5 accent-current"
            />
            {t('messaging.noteToggle')}
          </label>
        ) : null}
      </div>
    </form>
  );
}

/**
 * The colour, the car and the hire a thread is currently about, as one
 * line.
 *
 * The colour leads because it is the segment that identifies: make and
 * model narrow a fleet to three cars, and "Silver" picks the one — which
 * is how an owner with three Noahs talks about them, and how a renter in a
 * car park finds one. Dropped rather than substituted when the car has
 * none on file: "Unknown · Toyota Noah" is worse than "Toyota Noah".
 *
 * Null for a conversation with no subject at all — a support thread
 * somebody opened out of the blue — where the caller falls back to the
 * thread's own reference.
 */
export function threadSubject(thread: MessageThread): string | null {
  const car = thread.vehicle
    ? `${thread.vehicle.year} ${thread.vehicle.make} ${thread.vehicle.model}`
    : null;
  return (
    [thread.vehicle?.colour, car, thread.booking?.reference].filter(Boolean).join(' · ') || null
  );
}

export interface ThreadContextBarProps {
  thread: MessageThread;
  /** Where the car goes, so each app keeps its own routing. */
  as?: (children: ReactNode, className: string) => ReactNode;
  className?: string;
}

/**
 * What the open conversation is about, under its head.
 *
 * It exists because the head stopped being able to say so. One thread now
 * spans every hire between these two, so the name at the top names a
 * person and this names the car — and somebody who arrived from a booking
 * needs to see, immediately, that they are in the right place.
 *
 * Renders nothing when there is no subject: a support thread about
 * nothing in particular should not draw an empty strip.
 */
export function ThreadContextBar({ thread, as, className }: ThreadContextBarProps) {
  const { t } = useTranslation('common');
  const subject = threadSubject(thread);
  if (!subject) return null;

  const content = (
    <>
      <Car className="size-4 shrink-0 text-text-muted" aria-hidden />
      <span className="min-w-0 flex-1 truncate">{subject}</span>
      {thread.booking ? (
        <span className="shrink-0 text-[11px] uppercase tracking-wide text-text-subtle">
          {t(`bookingStatus.${thread.booking.status}`, { defaultValue: '' })}
        </span>
      ) : null}
    </>
  );

  const classes = cn(
    'flex w-full items-center gap-2 border-b border-border-default bg-surface-sunken px-4 py-2 text-left text-xs text-text',
    as && 'transition-colors hover:bg-surface-hover',
    className,
  );

  return as ? as(content, classes) : <div className={classes}>{content}</div>;
}

export interface ThreadRowProps {
  thread: MessageThread;
  active?: boolean;
  /** What the row links to, so each app keeps its own routing. */
  as?: (children: ReactNode, className: string) => ReactNode;
  className?: string;
}

/** `Raymond Tetteh` → `RT`. Two letters, from the first and last word. */
export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const letters = words.length > 1 ? [words[0]![0], words.at(-1)![0]] : [words[0]?.[0]];
  return letters.join('').toUpperCase() || '?';
}

/**
 * Who the conversation is with, as a disc: initials for a person, the
 * brand's own mark for Zuruflex, so support stands out down a list of people.
 */
export function ThreadAvatar({
  thread,
  className,
}: {
  thread: Pick<MessageThread, 'with'>;
  className?: string;
}) {
  const flex = thread.with.kind === 'ZURUFLEX';
  return (
    <span
      aria-hidden
      className={cn(
        'grid size-10 shrink-0 place-items-center rounded-full text-xs font-semibold',
        flex ? 'bg-accent text-accent-text' : 'bg-surface-active text-text',
        className,
      )}
    >
      {flex ? 'F' : initialsOf(thread.with.name)}
    </span>
  );
}

/** One line in the inbox: who, about what, when, and how much is unread. */
export function ThreadRow({ thread, active, as, className }: ThreadRowProps) {
  const { t, i18n } = useTranslation('common');
  const locale = toLocale(i18n.language);
  const unread = thread.unreadCount > 0;

  const content = (
    <div className="flex items-start gap-3">
      <ThreadAvatar thread={thread} />

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <p className="truncate text-sm font-semibold text-text">
            {thread.with.kind === 'ZURUFLEX' ? t('messaging.zuruflex') : thread.with.name}
          </p>
          <time
            className={cn(
              'shrink-0 text-[11px]',
              unread ? 'font-semibold text-text' : 'text-text-muted',
            )}
            dateTime={thread.lastMessageAt}
          >
            {formatListTime(thread.lastMessageAt, locale)}
          </time>
        </div>

        <div className="mt-0.5 flex items-center justify-between gap-2">
          {/*
            Whose last word it was. Without it the row says what was said
            and not who said it, so a thread you answered an hour ago and
            one waiting on you look identical — which is the question
            somebody scanning an inbox is actually asking.

            Interpolated through the catalogue rather than glued together
            here: French wants a narrow space before its colon, and a
            prefix assembled with `+` cannot have one.
          */}
          <p
            className={cn('truncate text-xs', unread ? 'font-medium text-text' : 'text-text-muted')}
          >
            {thread.preview
              ? thread.previewIsMine
                ? t('messaging.previewFromYou', { preview: thread.preview })
                : thread.preview
              : t('messaging.noMessages')}
          </p>

          {/*
            Whether the other end has read it, at the far end of the line
            it is about.

            Only under my own last word — a tick beside something they
            wrote would be telling them what they already know — and the
            same pair of glyphs, the same two colours and the same rule
            (`readBySeq >= messageSeq`) that `Stamp` draws on the bubble.
            A row and the thread it opens must not disagree about whether
            a message landed.

            `shrink-0` on both marks, so a long preview truncates rather
            than squeezing them out of the row.
          */}
          {thread.preview && thread.previewIsMine ? (
            thread.readBySeq >= thread.messageSeq ? (
              <CheckCheck className="size-3.5 shrink-0 text-info" aria-hidden />
            ) : (
              <Check className="size-3 shrink-0 text-text-subtle" aria-hidden />
            )
          ) : null}

          <UnreadDot
            count={thread.unreadCount}
            label={t('messaging.unread', { count: thread.unreadCount })}
          />
        </div>

        {/*
          The colour and the car before the reference: one thread now
          carries every hire these two have had, so the row has to say which
          one it is currently about, and "Silver · 2019 Toyota Corolla" is
          what somebody recognises. The reference is what they would read
          down the phone.
        */}
        <p className="mt-1 truncate text-[10.5px] uppercase tracking-wide text-text-subtle">
          {threadSubject(thread) ?? thread.reference}
        </p>
      </div>
    </div>
  );

  const classes = cn(
    'block w-full rounded-card px-3 py-3 text-left transition-colors',
    active ? 'bg-surface-active' : 'hover:bg-surface-hover',
    className,
  );

  return as ? as(content, classes) : <div className={classes}>{content}</div>;
}

/**
 * When, as a list reads it: the time for today, the day for this week, the
 * date beyond that. A full timestamp on every row is noise a column wide.
 */
function formatListTime(value: string, locale: ReturnType<typeof toLocale>): string {
  const when = new Date(value);
  const now = new Date();
  const tag = intlTagFor(locale);
  if (sameDay(when, now)) {
    return new Intl.DateTimeFormat(tag, { timeStyle: 'short' }).format(when);
  }
  const days = (now.getTime() - when.getTime()) / 86_400_000;
  if (days < 6) return new Intl.DateTimeFormat(tag, { weekday: 'short' }).format(when);
  return new Intl.DateTimeFormat(tag, { day: 'numeric', month: 'short' }).format(when);
}
